/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Let a teacher choose one backend-authorized draft Activity and copy one question into it.
 * @state-owner local target selection; TanStack Query owns targets and copy command state
 * @allowed-imports React; same-module queries and mutations; shared modal, overlay, translator and toast
 * @forbidden direct API client; organization authorization inference; central routing; shared i18n dictionaries
 * @public-api activities editor modal
 * @side-effects creates one independent Activity item and invalidates activities queries
 * @tests activities query and mutation contract tests; workspace Playwright flows
 */

import { useState } from 'react';
import { ModalDocument, OptionCard, SectionLabel } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useCreateActivityItemCopy, useRetryActivityItemCopy } from '../api/activities.mutations';
import { useActivityCopyTargets } from '../api/activities.queries';
import type { ActivityItemCopyResult } from '../model/types';

interface CopyActivityItemDialogProps {
  contextId: string;
  sourceActivityId: string;
  sourceActivityItemId: string;
  sourceItemRevision: number;
}

export function CopyActivityItemDialog({
  contextId,
  sourceActivityId,
  sourceActivityItemId,
  sourceItemRevision,
}: CopyActivityItemDialogProps) {
  const t = useTranslator();
  const { closeModal } = useOverlay();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const targets = useActivityCopyTargets(sourceActivityItemId);
  const copyItem = useCreateActivityItemCopy();
  const retryCopy = useRetryActivityItemCopy();
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [failedCopy, setFailedCopy] = useState<ActivityItemCopyResult | null>(null);
  const selectedTarget = targets.data?.items.find((item) => item.id === selectedTargetId);

  const submit = () => {
    if (!selectedTarget) return;
    copyItem.mutate(
      {
        contextId,
        sourceActivityId,
        sourceActivityItemId,
        expectedSourceItemRevision: sourceItemRevision,
        targetActivityId: selectedTarget.id,
        expectedTargetActivityRevision: selectedTarget.revision,
        idempotencyKey: crypto.randomUUID(),
      },
      {
        onSuccess: (result) => {
          if (result.state === 'failed') {
            setFailedCopy(result);
            notify(t('複製失敗，請稍後再試'));
            return;
          }
          notify(result.state === 'succeeded' ? t('題目已複製') : t('正在背景複製題目'));
          closeModal();
        },
        onError: () => notify(t('複製失敗，請稍後再試')),
      },
    );
  };

  const retry = () => {
    if (!failedCopy) return;
    retryCopy.mutate(failedCopy.copyId, {
      onSuccess: (result) => {
        if (result.state === 'failed') {
          setFailedCopy(result);
          notify(t('複製仍未完成，請稍後再試'));
          return;
        }
        notify(result.state === 'succeeded' ? t('題目已複製') : t('正在背景重新複製題目'));
        closeModal();
      },
      onError: () => notify(t('重新複製失敗，請稍後再試')),
    });
  };

  return (
    <ModalDocument
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button
            className="bt pri"
            disabled={failedCopy ? retryCopy.isPending : !selectedTarget || copyItem.isPending}
            type="button"
            onClick={failedCopy ? retry : submit}
          >
            {failedCopy
              ? retryCopy.isPending
                ? t('重新複製中…')
                : t('重新複製')
              : copyItem.isPending
                ? t('複製中…')
                : t('複製題目')}
          </button>
        </>
      }
      maxWidth={560}
      subtitle={t('目標題目會重新核准，並產生自己的解題計畫。')}
      title={t('複製到 Activity')}
    >
      <SectionLabel first>{t('選擇目標 Activity')}</SectionLabel>
      {failedCopy ? (
        <div className="eqbox" role="alert">
          {t('檔案處理沒有完成；安全的複製紀錄已保留，可以從這裡重新嘗試。')}
        </div>
      ) : null}
      {targets.isPending ? (
        <VispoLoading className="vispo-loading--compact" label={t('正在載入可用的 Activity…')} />
      ) : null}
      {targets.isError ? (
        <div className="eqbox" style={{ fontSize: 14, lineHeight: '22px' }}>
          {t('無法載入可用的 Activity，請稍後再試。')}
        </div>
      ) : null}
      {targets.data && targets.data.items.length === 0 ? (
        <p className="nlempty">{t('目前沒有其他可編輯的草稿 Activity。')}</p>
      ) : null}
      {targets.data?.items.map((target) => (
        <OptionCard
          description={
            target.workspaceKind === 'personal'
              ? t('個人工作區')
              : `${t('機構')} · ${target.organizationName}`
          }
          key={target.id}
          on={selectedTargetId === target.id}
          title={target.title}
          onSelect={() => setSelectedTargetId(target.id)}
        />
      ))}
    </ModalDocument>
  );
}
